import { Button } from "@atoms/button/button";
import { DropDownAtom, DropDownMenuType } from "@atoms/dropdown";
import { Unit } from "@atoms/input/input-unit";
import {
  FormContextContext,
  FormControllerType,
} from "@components/form/formcontext";
import { InputButton } from "@components/input-button";
import { useArticle } from "@features/articles/hooks/use-articles";
import { Articles } from "@features/articles/types/types";
import {
  formatMargin,
  getArticleMaxCost,
  getCostEstimate,
  getMarginFromPrice,
} from "@features/articles/utils";
import {
  useMarginBase,
  useMarginMode,
} from "@features/clients/state/use-clients";
import { InvoiceLine, Invoices } from "@features/invoices/types/types";
import { getRoute, ROUTES } from "@features/routes";
import { useInvoiceMaps } from "@features/invoices/hooks/use-invoice-maps";
import {
  formatAmount,
  formatQuantity,
  getTextFromHtml,
} from "@features/utils/format/strings";
import { useEffectChange } from "@features/utils/hooks/use-changed-effect";
import {
  CheckIcon,
  LockClosedIcon,
  ReceiptPercentIcon,
  XCircleIcon,
} from "@heroicons/react/16/solid";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bars3BottomLeftIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { EditorInput } from "@molecules/editor-input";
import {
  Badge,
  Box,
  Card,
  Code,
  Flex,
  Heading,
  HoverCard,
  Inset,
  Link,
  Separator,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import {
  ArticlesDetailsPage,
  frequencyOptions,
} from "@views/client/modules/articles/components/article-details";
import _ from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { useDrag, useDragLayer, useDrop } from "react-dnd";
import { useSetRecoilState } from "recoil";
import { twMerge } from "tailwind-merge";
import { getArticleIcon } from "../../../articles/components/article-icon";
import { getTvaValue } from "../../utils";
import { CompletionTags } from "./components/completion-tags";
import { InvoiceDiscountInput } from "./components/discount-input";
import { InvoiceLineArticleInput } from "./components/line-article";
import { InvoiceLinePriceInput } from "./components/line-price";
import { InvoiceLineQuantityInput } from "./components/line-quantity";

export const isSellDocument = (invoice?: Pick<Invoices, "type"> | null) =>
  ["quotes", "invoices", "credit_notes"].includes(invoice?.type || "");

export const getCorrectPrice = (
  article: Articles | null,
  invoice: Invoices,
) => {
  const isSupplierRelated = [
    "supplier_credit_notes",
    "supplier_quotes",
    "supplier_invoices",
  ].includes(invoice?.type || "");
  const correctPrice = isSupplierRelated
    ? article?.suppliers_details?.[invoice?.supplier]?.price ||
      article?.suppliers_details?.["custom"]?.price
    : article?.price || 0;
  return parseFloat(correctPrice as any);
};
export const InvoiceLineInput = (props: {
  invoice?: Invoices;
  value?: InvoiceLine;
  onChange?: (v: InvoiceLine) => void;
  ctrl?: FormControllerType<InvoiceLine>;
  onDuplicate?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove?: () => void;
  onCreateGroup?: () => void; // Create a group of articles from this line
  hidePrices?: boolean; // Prices of this line are not displayed on the document (group option)
  readonly?: boolean;
}) => {
  const formContext = useContext(FormContextContext);
  const { getTvaOptions } = useInvoiceMaps();

  const value = props.ctrl?.value || props.value || ({} as InvoiceLine);
  const onChange = props.ctrl?.onChange || props.onChange;
  const readonly = props.readonly ?? formContext.readonly;

  const { article } = useArticle(value.article || "");

  const setMenu = useSetRecoilState(DropDownAtom);
  const [{ dragging }, dragRef] = useDrag(
    () => ({
      canDrag: !readonly,
      type: INVOICE_LINE_DND,
      item: value,
      collect: (monitor) => ({
        dragging: monitor.isDragging() ? true : false,
      }),
    }),
    [value],
  );
  const [deleted, setDeleted] = useState(true);
  useEffect(() => {
    // Create animated entrance
    setDeleted(false);
  }, []);

  const { otherDragging } = useDragLayer((monitor) => ({
    otherDragging: monitor.isDragging(),
  }));

  const hasOptionalArticles = (props.invoice?.content || []).some(
    (a) => a.optional,
  );
  const isSeparation =
    value.type === "separation" || value.type === "correction";

  const previousArticleSupplierValues = useRef("");
  useEffectChange(() => {
    if (
      props.invoice?.supplier &&
      (article?.id || !article?.price) &&
      !readonly
    ) {
      const previousValue = previousArticleSupplierValues.current;
      const key = props.invoice?.supplier + "-" + article?.id;
      if (key !== previousArticleSupplierValues.current) {
        previousArticleSupplierValues.current = key;
        if (previousValue) {
          // If there was a value, but this value changed, then we can reset the prices to put the right ones
          onChange?.({
            ...value,
            unit_price: getCorrectPrice(article!, props.invoice!),
          });
        }
      }
    }
  }, [article?.id, props.invoice?.supplier]);

  const marginMode = useMarginMode();
  const marginBase = useMarginBase();
  const lineCost = getArticleMaxCost(article);
  const showMargin =
    marginMode &&
    isSellDocument(props.invoice) &&
    value.type !== "correction" &&
    !!lineCost;
  const lineTotalHT =
    (value.quantity || 0) * (value.unit_price || 0) -
    (value.discount?.mode === "percentage"
      ? (value.quantity || 0) *
        (value.unit_price || 0) *
        ((parseFloat(value.discount.value as any) || 0) / 100)
      : value.discount?.mode === "amount"
        ? parseFloat(value.discount.value as any) || 0
        : 0);
  const totalMargin =
    showMargin && value.quantity
      ? getMarginFromPrice(
          lineTotalHT / value.quantity,
          lineCost,
          marginBase,
        )
      : null;

  const hasRefSupplier =
    !!article?.supplier_reference ||
    getCostEstimate(article || undefined, false, 1, "");

  return (
    <>
      <div
        ref={dragRef}
        className={twMerge(
          "relative flex w-full items-center opacity-100 transition-all mb-3 group/invoice-line",
          (deleted || dragging) && "max-h-0 opacity-0 !m-0",
        )}
      >
        {!readonly && (
          <div
            className={twMerge(
              "absolute w-5 h-5 flex items-center justify-start cursor-grab opacity-0 group-hover/invoice-line:opacity-100 -left-4",
              otherDragging && !dragging && "opacity-0",
            )}
          >
            <EllipsisVerticalIcon className="w-4 h-4 opacity-25 -ml-0.5" />
            <EllipsisVerticalIcon className="w-4 h-4 opacity-25 -ml-3" />
          </div>
        )}
        <Card
          variant="ghost"
          className={twMerge(
            "w-full p-0 border m-0 dark:border-slate-700",
            !value.optional_checked &&
              value.optional &&
              "border-dashed shadow-none",
          )}
        >
          <Flex direction="column">
            <Flex align="stretch">
              <Box flexGrow="1">
                <HoverableArticle
                  readonly={readonly}
                  value={value}
                  article={article}
                  onChange={onChange}
                  ctrl={props.ctrl}
                  invoice={props.invoice}
                  onCreateGroup={props.onCreateGroup}
                />
              </Box>
              {!isSeparation && hasRefSupplier && (
                <Box
                  className={twMerge(
                    "text-right w-1/6 shrink-0 p-3",
                    !value.optional_checked &&
                      value.optional &&
                      "border-dashed",
                  )}
                >
                  <Text as="div" size="2" weight="bold" className="select-all">
                    {article?.supplier_reference}
                  </Text>
                  {getCostEstimate(article || undefined, false, 1, "") && (
                    <Text as="div" color="gray" size="2">
                      {getCostEstimate(article || undefined, false)} HT
                    </Text>
                  )}
                </Box>
              )}
              {!isSeparation && (
                <Box
                  className={twMerge(
                    "text-right w-1/6 shrink-0 border-l dark:border-slate-700",
                    !value.optional_checked &&
                      value.optional &&
                      "border-dashed",
                  )}
                >
                  <InputButton
                    readonly={readonly}
                    theme="invisible"
                    className="rounded-none  h-full w-full flex grow p-3 m-0 box-border text-right justify-end"
                    label="Quantité"
                    placeholder="Quantité"
                    content={() => (
                      <InvoiceLineQuantityInput {...props} article={article} />
                    )}
                    value={formatQuantity(value.quantity, value.unit)}
                  >
                    <Text as="div" size="2" weight="bold">
                      {formatQuantity(value.quantity || 0, value.unit)}{" "}
                      <Unit unit={value?.unit} />
                    </Text>
                    <Text as="div" color="gray" size="2">
                      {value.subscription &&
                      frequencyOptions?.find(
                        (a) => a.value === value.subscription,
                      )?.label ? (
                        <Badge size="1" color="blue">
                          {
                            frequencyOptions?.find(
                              (a) => a.value === value.subscription,
                            )?.label
                          }
                        </Badge>
                      ) : (
                        "-"
                      )}
                    </Text>
                  </InputButton>
                </Box>
              )}
              {(!isSeparation || value.type === "correction") && (
                <Box
                  className={twMerge(
                    "text-right w-1/6 shrink-0 border-l dark:border-slate-700",
                    !value.optional_checked &&
                      value.optional &&
                      "border-dashed",
                    props.hidePrices && "opacity-50",
                  )}
                  title={
                    props.hidePrices
                      ? "Prix masqué sur le document (seul le total du groupe est affiché)"
                      : undefined
                  }
                >
                  <PriceInput
                    readonly={readonly}
                    article={article}
                    value={value}
                    onChange={(v) => onChange?.(v)}
                    ctrl={props.ctrl}
                    invoice={props.invoice!}
                  >
                    <Text as="div" size="2" weight="bold">
                      {formatAmount(
                        value.unit_price || 0,
                        props.invoice?.currency || "EUR",
                      )}
                    </Text>
                    <Text
                      as="div"
                      color="gray"
                      size="2"
                      className="whitespace-nowrap"
                    >
                      {getTvaValue(value.tva || "0")
                        ? "TVA " +
                          Math.round(getTvaValue(value.tva || "0") * 10000) /
                            100 +
                          "%"
                        : getTvaOptions(value.tva).find(
                            (a) => a.value === value.tva,
                          )?.label}
                    </Text>
                  </PriceInput>
                </Box>
              )}
              {(!isSeparation || value.type === "correction") && (
                <Box
                  className={twMerge(
                    "text-right w-1/5 shrink-0 border-l dark:border-slate-700",
                    !value.optional_checked &&
                      value.optional &&
                      "border-dashed",
                    props.hidePrices && "opacity-50",
                  )}
                  title={
                    props.hidePrices
                      ? "Prix masqué sur le document (seul le total du groupe est affiché)"
                      : undefined
                  }
                >
                  <PriceInput
                    readonly={readonly}
                    article={article}
                    value={value}
                    onChange={(v) => onChange?.(v)}
                    ctrl={props.ctrl}
                    invoice={props.invoice!}
                  >
                    <Text as="div" size="2" weight="bold">
                      {formatAmount(
                        (
                          (value.quantity || 0) * (value.unit_price || 0)
                        ).toFixed(2),
                        props.invoice?.currency || "EUR",
                      )}
                    </Text>
                    {["quotes", "invoices", "credit_notes"].includes(
                      props.invoice?.type || "",
                    ) &&
                      value.type !== "correction" && (
                        <Tooltip
                          content={
                            totalMargin === null
                              ? "Coût estimé (HT) maximum pour cette ligne"
                              : "Coût estimé (HT) maximum et marge de cette ligne"
                          }
                        >
                          <Text as="div" color="gray" size="2">
                            {totalMargin === null && "Coût "}
                            {_.last(
                              getCostEstimate(
                                article || undefined,
                                false,
                                value.quantity || 1,
                              ).split("-"),
                            ) || "non renseigné"}
                            {totalMargin !== null &&
                              `/Marge ${formatMargin(totalMargin)}`}
                          </Text>
                        </Tooltip>
                      )}
                    <Text
                      as="div"
                      color="gray"
                      size="2"
                      className="whitespace-nowrap"
                    >
                      {!!value.discount?.value && (
                        <Code color="crimson" className="ml-2">
                          -{" "}
                          {value.discount?.mode === "amount"
                            ? formatAmount(value.discount?.value || 0, "EUR")
                            : value.discount?.value + "%"}
                        </Code>
                      )}
                    </Text>
                  </PriceInput>
                </Box>
              )}
            </Flex>
            {(!(
              value.type === "separation" ||
              !["quotes", "supplier_quotes"].includes(props.invoice?.type || "")
            ) ||
              !readonly) && (
              <Flex
                align="center"
                gap="3"
                className={twMerge(
                  "p-3 border-t dark:border-slate-700",
                  !value.optional_checked && value.optional && "border-dashed",
                )}
              >
                <Box flexGrow="1">
                  {!(value.optional && !value.optional_checked) && (
                    <>
                      {(props.invoice?.type === "supplier_quotes" ||
                        props.invoice?.type === "quotes") && (
                        <div className="w-32 shrink-0 flex items-center justify-start mr-2">
                          {!isSeparation && (
                            <CompletionTags
                              invoice={props.invoice!}
                              lines={[value]}
                              overflow
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Box>

                {value.optional && (
                  <Text size="2">
                    Ligne optionelle{" "}
                    {value.optional_checked && (
                      <Text weight="bold">selectionnée</Text>
                    )}
                    {!value.optional_checked && (
                      <Text weight="bold">non selectionnée</Text>
                    )}
                  </Text>
                )}

                {hasOptionalArticles && !isSeparation && (
                  <Button
                    disabled={readonly}
                    onClick={(e) =>
                      setMenu({
                        target: e.currentTarget,
                        menu: [
                          {
                            label: "Option cochée",
                            icon: (p) => <CheckCircleIcon {...p} />,
                            onClick: () => {
                              onChange?.({
                                ...value,
                                optional: true,
                                optional_checked: true,
                              });
                            },
                          },
                          {
                            label: "Option décochée",
                            icon: (p) => <XCircleIcon {...p} />,
                            onClick: () => {
                              onChange?.({
                                ...value,
                                optional: true,
                                optional_checked: false,
                              });
                            },
                          },
                          {
                            type: "divider",
                          },
                          {
                            label: "Article non optionnel",
                            icon: (p) => <LockClosedIcon {...p} />,
                            onClick: () => {
                              onChange?.({
                                ...value,
                                optional: false,
                                optional_checked: false,
                              });
                            },
                          },
                        ],
                      })
                    }
                    theme="invisible"
                    size="xs"
                    icon={({ className }) =>
                      value.optional ? (
                        <div className="border-slate-100 border rounded w-4 h-4 flex items-center justify-center">
                          {value.optional_checked ? (
                            <CheckIcon className="w-4 h-4" />
                          ) : (
                            <div className="w-1 h-1 dark:bg-white bg-black dark:bg-white opacity-50" />
                          )}
                        </div>
                      ) : (
                        <LockClosedIcon
                          className={twMerge(className, "opacity-25")}
                        />
                      )
                    }
                  />
                )}
                {!readonly && (
                  <>
                    {props.onRemove && (
                      <Button
                        danger
                        theme="invisible"
                        data-tooltip="Retirer la ligne"
                        icon={(p) => <TrashIcon {...p} />}
                        onClick={() => {
                          setDeleted(true);
                          setTimeout(props.onRemove!, 300);
                        }}
                        size="xs"
                      />
                    )}
                    <Button
                      onClick={(e) =>
                        setMenu({
                          target: e.currentTarget,
                          menu: [
                            ...((!isSeparation
                              ? [
                                  {
                                    label: value.discount?.value
                                      ? "Retirer la réduction"
                                      : "Ajouter une réduction",
                                    icon: (p) => <ReceiptPercentIcon {...p} />,
                                    onClick: () => {
                                      onChange?.({
                                        ...value,
                                        discount: value.discount?.value
                                          ? undefined
                                          : {
                                              value: 10,
                                              mode: "percentage",
                                            },
                                      });
                                    },
                                  },
                                  ...((props.invoice?.type === "quotes"
                                    ? [
                                        {
                                          label: value.optional
                                            ? "Article obligatoire"
                                            : "Article optionnel",
                                          icon: (p) =>
                                            value.optional ? (
                                              <LockClosedIcon {...p} />
                                            ) : (
                                              <CheckCircleIcon {...p} />
                                            ),
                                          onClick: () => {
                                            onChange?.({
                                              ...value,
                                              optional: !value.optional,
                                            });
                                          },
                                        },
                                      ]
                                    : []) as DropDownMenuType),
                                  { type: "divider" },
                                ]
                              : []) as DropDownMenuType),
                            {
                              label: "Dupliquer",
                              icon: (p) => <DocumentDuplicateIcon {...p} />,
                              onClick: props.onDuplicate,
                            },
                            {
                              label: "Déplacer vers le haut",
                              icon: (p) => <ArrowUpIcon {...p} />,
                              onClick: props.onMoveUp,
                            },
                            {
                              label: "Déplacer vers le bas",
                              icon: (p) => <ArrowDownIcon {...p} />,
                              onClick: props.onMoveDown,
                            },
                          ],
                        })
                      }
                      theme="invisible"
                      icon={(p) => <EllipsisHorizontalIcon {...p} />}
                      size="xs"
                    />
                  </>
                )}
              </Flex>
            )}
          </Flex>
        </Card>
      </div>
    </>
  );
};

export const INVOICE_LINE_DND = "invoice-line";
export const INVOICE_GROUP_DND = "invoice-group";

export const DropInvoiceLine = (props: {
  onMove: (item: InvoiceLine) => void;
  accept?: string[]; // Defaults to lines only
  size?: "default" | "small"; // Small zones are used inside groups and around them
}) => {
  const accept = props.accept || [INVOICE_LINE_DND];
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept,
      drop: (value: InvoiceLine, monitor) => {
        // Nested drop zones: only the deepest one handles the drop
        if (monitor.didDrop()) return;
        props.onMove(value);
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver({ shallow: true }),
      }),
    }),
    [props.onMove, accept.join(",")],
  );

  const { isDragging, itemType } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    itemType: monitor.getItemType(),
  }));
  const accepts = isDragging && accept.includes(itemType as string);

  return (
    <div className={twMerge("relative w-full h-0")}>
      <div
        ref={drop}
        className={twMerge(
          "absolute w-full h-px top-0 transition-all delay-400 z-10",
          accepts &&
            (props.size === "small"
              ? "h-10 -top-6"
              : "h-24 -top-14 -left-1/2 w-[200%]"),
          isOver && (props.size === "small" ? "h-10" : "h-24"),
          isDragging && !accepts && "pointer-events-none",
        )}
      ></div>
      <div
        className={twMerge(
          "absolute w-full h-0.5 -top-[5px] opacity-0 transition-all bg-slate-300 rounded-full pointer-events-none shadow-sm",
          isOver && "opacity-100",
        )}
      />
    </div>
  );
};

const PriceInput = ({
  readonly,
  article,
  value,
  onChange,
  ctrl,
  invoice,
  children,
}: {
  readonly?: boolean;
  article?: Articles | null;
  value: InvoiceLine;
  onChange?: (v: InvoiceLine) => void;
  ctrl?: FormControllerType<InvoiceLine>;
  invoice: Invoices;
  children?: React.ReactNode;
}) => {
  return (
    <InputButton
      readonly={readonly}
      theme="invisible"
      className="rounded-none h-full w-full flex grow p-3 m-0 box-border text-right justify-end"
      label="Prix et TVA"
      placeholder="Prix et TVA"
      content={() => (
        <>
          <InvoiceLinePriceInput
            article={article}
            value={value}
            onChange={onChange}
            ctrl={ctrl}
            invoice={invoice}
          />
          {value.type !== "correction" && (
            <>
              <br />
              <InvoiceDiscountInput
                onChange={(d) => onChange?.({ ...value, discount: d })}
                value={value.discount}
              />
            </>
          )}
        </>
      )}
      value={
        _.isNumber(value.unit_price) ||
        !_.isNaN(parseFloat(value.unit_price as any))
      }
    >
      {children}
    </InputButton>
  );
};

const HoverableArticle = ({
  readonly,
  value,
  article,
  ...props
}: {
  readonly?: boolean;
  value: InvoiceLine;
  article?: Articles | null;
  onChange?: (v: InvoiceLine) => void;
  ctrl?: FormControllerType<InvoiceLine>;
  invoice?: Invoices;
  onCreateGroup?: () => void;
}) => {
  const isSeparation =
    value.type === "separation" || value.type === "correction";

  const hasDetails = readonly && !!article;

  return (
    <HoverCard.Root>
      <HoverCard.Trigger>
        <Link
          href={
            hasDetails
              ? getRoute(ROUTES.ProductsView, { id: article?.id })
              : undefined
          }
        >
          <InputButton
            theme="invisible"
            readonly={readonly}
            className="rounded-none flex grow p-3 m-0 h-full w-full box-border text-left justify-start"
            autoFocus={!readonly && !(value.name || value.description)}
            placeholder={!isSeparation ? "Article" : "Texte libre"}
            icon={(p) =>
              isSeparation ? (
                <Bars3BottomLeftIcon {...p} />
              ) : (
                getArticleIcon(article?.type)(p)
              )
            }
            empty="Vide"
            content={({ close }) => (
              <InvoiceLineArticleInput
                {...props}
                invoice={props.invoice!}
                article={article}
                close={close}
              />
            )}
            value={value.description || value.name || article?.name}
          >
            <Text
              as="div"
              size="2"
              weight="bold"
              className={twMerge(
                "overflow-hidden text-ellipsis",
                !readonly && "line-clamp-1",
              )}
            >
              {value.name || article?.name}
            </Text>
            <Text
              as="div"
              color="gray"
              size="2"
              className={twMerge(
                "overflow-hidden text-ellipsis",
                !readonly && "line-clamp-1",
              )}
            >
              {getTextFromHtml(
                value.description || article?.description || "-",
              ).trim() && (
                <EditorInput
                  disabled
                  placeholder=""
                  value={value.description || article?.description || "-"}
                />
              )}
            </Text>
          </InputButton>
        </Link>
      </HoverCard.Trigger>
      {hasDetails && (
        <HoverCard.Content className="overflow-auto" maxHeight="30vh">
          <Inset>
            <div className="p-4">
              <Heading size="2">{value.name}</Heading>
              <Text as="div" color="gray" size="2">
                {getTextFromHtml(
                  value.description || article?.description || "-",
                ).trim() && (
                  <EditorInput
                    placeholder=""
                    value={value.description || article?.description || "-"}
                  />
                )}
              </Text>
            </div>
            <Separator size="4" />
            <div className="p-4">
              <ArticlesDetailsPage readonly id={article?.id} />
            </div>
          </Inset>
        </HoverCard.Content>
      )}
    </HoverCard.Root>
  );
};
